import { useQuery } from '@tanstack/react-query';
import {
  fetchIncidentTagSafetyEvents,
  sortSafetyEventsByTimestamp,
  type IncidentTagSafetyEventsParams,
  type SafetyEvent,
} from '@apis/reports';

interface Args extends IncidentTagSafetyEventsParams {
  /** Caller-supplied gate (typically `entityKind === 'tag' && !!incidentData`). */
  enabled?: boolean;
}

/**
 * Subject-card safety events for the Incident Report. Tag-filtered, all
 * areas/floors, 1-hour window centred on the incident instant. Trusstor
 * parity: `getSafetyEventsByTagIdInOneHourTimeframe`.
 *
 * Distinct from `useRangeSafetyEventsQuery` (project-wide, area/floor-filtered)
 * which the per-stop SAFETY EVENTS section continues to use.
 */
export function useIncidentTagSafetyEventsQuery(
  args: Args,
): ReturnType<typeof useQuery<SafetyEvent[]>> {
  return useQuery({
    queryKey: [
      'reports',
      'incident-report',
      'tag-safety-events',
      args.siteId,
      args.tagId,
      args.atTimestamp,
    ],
    queryFn: ({ signal }) =>
      fetchIncidentTagSafetyEvents(
        {
          siteId: args.siteId,
          tagId: args.tagId,
          atTimestamp: args.atTimestamp,
        },
        signal,
      ),
    // Chronological order across the whole 1-hour window — the chip grid
    // reads from earliest to latest event so the UI matches the timeline
    // of the rail below it.
    select: sortSafetyEventsByTimestamp,
    enabled:
      !!args.siteId &&
      !!args.tagId &&
      args.atTimestamp > 0 &&
      (args.enabled ?? true),
    staleTime: 60 * 1000,
  });
}
